import { supabase } from './supabase';

export interface ReEntryEligibility {
  is_eligible: boolean;
  current_re_entry_count: number;
  max_re_entries: number;
  re_entry_fee: number;
  reason: string;
}

export interface ReEntryStatus {
  has_original_registration: boolean;
  re_entry_count: number;
  can_request_re_entry: boolean;
  has_pending_re_entry: boolean;
  has_completed_re_entry: boolean;
}

export interface ReEntryRequest {
  id: string;
  original_registration_id: string;
  event_id: string;
  category_id: string;
  user_id: string;
  request_date: string;
  request_reason?: string;
  status: 'pending' | 'approved' | 'rejected';
  approved_by?: string;
  approved_date?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Checks if a driver is eligible for re-entry
 * @param userId - The user's ID
 * @param eventId - The event ID
 * @param categoryId - The category ID
 * @returns Eligibility information
 */
export async function checkReEntryEligibility(
  userId: string,
  eventId: string,
  categoryId: string
): Promise<ReEntryEligibility> {
  try {
    const { data, error } = await supabase.rpc('check_re_entry_eligibility', {
      p_user_id: userId,
      p_event_id: eventId,
      p_category_id: categoryId
    });

    if (error) throw error;
    return data[0] || {
      is_eligible: false,
      current_re_entry_count: 0,
      max_re_entries: 1,
      re_entry_fee: 0,
      reason: 'Error checking eligibility'
    };
  } catch (error) {
    console.error('Error checking re-entry eligibility:', error);
    throw error;
  }
}

/**
 * Registers for re-entry directly
 * @param userId - The user's ID
 * @param eventId - The event ID
 * @param categoryId - The category ID
 * @returns Success status
 */
export async function registerReEntry(
  userId: string,
  eventId: string,
  categoryId: string
): Promise<boolean> {
  try {
    console.log('Attempting to register re-entry:', { userId, eventId, categoryId });
    
    const { data, error } = await supabase.rpc('register_re_entry', {
      p_user_id: userId,
      p_event_id: eventId,
      p_category_id: categoryId
    });

    if (error) {
      console.error('Supabase RPC error:', error);
      throw error;
    }
    
    console.log('Re-entry registration result:', data);
    return data;
  } catch (error) {
    console.error('Error registering for re-entry:', error);
    console.error('Error details:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code
    });
    throw error;
  }
}

/**
 * Gets comprehensive re-entry status for a user
 * @param userId - The user's ID
 * @param eventId - The event ID
 * @param categoryId - The category ID
 * @returns Re-entry status information
 */
export async function getUserReEntryStatus(
  userId: string,
  eventId: string,
  categoryId: string
): Promise<ReEntryStatus> {
  try {
    const { data, error } = await supabase.rpc('get_user_re_entry_status', {
      p_user_id: userId,
      p_event_id: eventId,
      p_category_id: categoryId
    });

    if (error) throw error;
    return data[0] || {
      has_original_registration: false,
      re_entry_count: 0,
      re_entry_status: 'none',
      can_request_re_entry: false,
      has_pending_request: false,
      has_approved_request: false,
      has_completed_re_entry: false
    };
  } catch (error) {
    console.error('Error getting re-entry status:', error);
    throw error;
  }
}

/**
 * Gets all re-entry registrations for an event (admin only)
 * @param eventId - The event ID
 * @returns Array of re-entry registrations with user information
 */
export async function getEventReEntryRegistrations(eventId: string): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('event_registrations')
      .select(`
        *,
        users!inner(
          full_name,
          email
        ),
        event_categories!inner(
          name,
          vehicle_type,
          registration_fee,
          global_category_id
        ),
        events!inner(
          name,
          event_date
        )
      `)
      .eq('event_id', eventId)
      .not('original_registration_id', 'is', null)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Get re-entry fees for each registration
    const registrationsWithFees = await Promise.all(
      (data || []).map(async (registration) => {
        if (registration.event_categories?.global_category_id) {
          const { data: globalCategory } = await supabase
            .from('global_categories')
            .select('re_entry_fee, allows_re_entry')
            .eq('id', registration.event_categories.global_category_id)
            .single();
          
          return {
            ...registration,
            re_entry_fee: globalCategory?.re_entry_fee || 0,
            allows_re_entry: globalCategory?.allows_re_entry || false
          };
        }
        return registration;
      })
    );

    return registrationsWithFees;
  } catch (error) {
    console.error('Error getting re-entry registrations:', error);
    throw error;
  }
}

/**
 * Approves a re-entry registration (admin only)
 * @param registrationId - The registration ID
 * @param approvedBy - The admin user ID who approved the registration
 * @returns Success status
 */
export async function approveReEntryRegistration(registrationId: string, approvedBy: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('approve_re_entry_registration', {
      p_registration_id: registrationId,
      p_approved_by: approvedBy
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error approving re-entry registration:', error);
    throw error;
  }
}

/**
 * Cancels a re-entry registration (admin only)
 * @param registrationId - The registration ID
 * @param cancelledBy - The admin user ID who cancelled the registration
 * @returns Success status
 */
export async function cancelReEntryRegistration(
  registrationId: string,
  cancelledBy: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('cancel_re_entry_registration', {
      p_registration_id: registrationId,
      p_cancelled_by: cancelledBy
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error cancelling re-entry registration:', error);
    throw error;
  }
}

/**
 * Gets re-entry requests for a specific user
 * @param userId - The user's ID
 * @returns Array of user's re-entry requests
 */
export async function getUserReEntryRequests(userId: string): Promise<ReEntryRequest[]> {
  try {
    const { data, error } = await supabase
      .from('re_entry_requests')
      .select(`
        *,
        events!inner(
          name,
          event_date
        ),
        event_categories!inner(
          name,
          vehicle_type
        )
      `)
      .eq('user_id', userId)
      .order('request_date', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting user re-entry requests:', error);
    throw error;
  }
} 

/**
 * Deletes a pending re-entry registration for a driver
 * @param registrationId - The registration ID
 * @param userId - The user's ID
 * @returns Success status
 */
export async function deletePendingReEntryRegistration(
  registrationId: string,
  userId: string
): Promise<boolean> {
  const { error } = await supabase
    .from('event_registrations')
    .delete()
    .eq('id', registrationId)
    .eq('user_id', userId);
  if (error) throw error;
  return true;
} 

/**
 * Deletes a re-entry registration and decrements the original's re_entry_count
 * @param registrationId - The registration ID
 * @param userId - The user's ID
 * @returns Success status
 */
export async function deleteReEntryRegistration(
  registrationId: string,
  userId: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc('delete_re_entry_registration', {
    p_registration_id: registrationId,
    p_user_id: userId
  });
  if (error) throw error;
  return data;
} 